import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mealLogsApi, type DaySummary, type HistoryOverview } from "../api/client";
import { PageHeader } from "../components/PageHeader";

function fullHistoryRange() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: "1970-01-01", to: fmt(yesterday) };
}

export function HistoryPage() {
  const [items, setItems] = useState<DaySummary[]>([]);
  const [overview, setOverview] = useState<HistoryOverview | null>(null);

  const getBalance = (day: DaySummary) => {
    if (day.target_kcal <= 0) return null;

    const diff = day.total_kcal - day.target_kcal;
    if (diff > 0) {
      return { label: `${diff} kcal オーバー`, className: "history-balance history-balance-over" };
    }
    if (diff < 0) {
      return {
        label: `${Math.abs(diff)} kcal アンダー`,
        className: "history-balance history-balance-under",
      };
    }
    return { label: "目標ぴったり", className: "history-balance history-balance-equal" };
  };

  const totalKcal = overview?.total_kcal ?? 0;
  const totalTargetKcal = overview?.total_target_kcal ?? 0;
  const cumulativeDiff = totalKcal - totalTargetKcal;
  const hasTrackedItems = overview !== null && totalTargetKcal > 0;
  const cumulativeLabel =
    cumulativeDiff > 0
      ? `${cumulativeDiff} kcal の借金`
      : cumulativeDiff < 0
        ? `${Math.abs(cumulativeDiff)} kcal の貯金`
        : "収支はちょうど 0 kcal";
  const cumulativeClassName =
    cumulativeDiff > 0
      ? "history-total-balance history-total-balance-over"
      : cumulativeDiff < 0
        ? "history-total-balance history-total-balance-under"
        : "history-total-balance history-total-balance-equal";

  useEffect(() => {
    const { from, to } = fullHistoryRange();
    mealLogsApi.range(from, to).then((rows) => {
      const nextOverview = rows.reduce<HistoryOverview>(
        (acc, row) => ({
          total_kcal: acc.total_kcal + row.total_kcal,
          total_target_kcal: acc.total_target_kcal + row.target_kcal,
        }),
        { total_kcal: 0, total_target_kcal: 0 }
      );

      // 新しい順
      setItems([...rows].reverse());
      setOverview(nextOverview);
    });
  }, []);

  return (
    <div className="page">
      <PageHeader title="過去の記録" titleClassName="page-title-half" />
      {hasTrackedItems ? (
        <div className="summary-card history-total-card">
          <div className="summary-target">これまでのトータル収支</div>
          <div className={cumulativeClassName}>{cumulativeLabel}</div>
          <div className="summary-target history-total-detail">
            合計 {totalKcal} kcal / 目標 {totalTargetKcal} kcal
          </div>
        </div>
      ) : null}
      {items.length === 0 ? (
        <p className="muted">記録がありません。</p>
      ) : (
        items.map((d) => {
          const balance = getBalance(d);

          return (
            <Link
              key={d.date}
              to={`/history/${d.date}`}
              className="list-item"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="list-item-main">
                <span className="list-item-title">{d.date}</span>
                <div className="history-list-meta">
                  <span className="list-item-sub history-weight-sub">
                    {d.min_weight_kg !== undefined && d.min_weight_kg !== null
                      ? `体重 ${d.min_weight_kg} kg`
                      : "体重 未記録"}
                  </span>
                </div>
                <div className="history-list-meta">
                  <span className="list-item-sub">{d.total_kcal} kcal</span>
                  {balance ? <span className={balance.className}>{balance.label}</span> : null}
                </div>
              </div>
              <span className="muted">›</span>
            </Link>
          );
        })
      )}
    </div>
  );
}
